import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { isTokenExpired, clearAuthData } from '../../utils/auth';
import tokenManager from '../../utils/tokenManager';
import deviceManager from '../../utils/deviceManager';
import { getErrorMessage } from '../../utils/errorHandler';

const API_URL = process.env.REACT_APP_AUTH_API_URL || 'http://localhost:5000/api/auth';
const user = JSON.parse(localStorage.getItem('user'));
const token = localStorage.getItem('token');

const initialState = {
  user: user ? user : null,
  token: token ? token : null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: '',
  requiresTwoFactor: false,
  requiresVerification: false,
  verificationEmail: '',
  refreshToken: tokenManager.getRefreshToken(),
  impersonating: false,
  originalUser: null,
};

export const checkAuth = createAsyncThunk('auth/checkAuth', async (_, thunkAPI) => {
  try {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));
    const impersonating = localStorage.getItem('impersonating') === 'true';
    const originalUser = JSON.parse(localStorage.getItem('originalUser'));

    if (!token || !user) {
      return thunkAPI.rejectWithValue('No auth data found');
    }

    if (isTokenExpired(token)) {
      clearAuthData();
      return thunkAPI.rejectWithValue('Token expired');
    }

    return { user, token, impersonating, originalUser };
  } catch (error) {
    clearAuthData();
    return thunkAPI.rejectWithValue('Auth check failed');
  }
});

export const register = createAsyncThunk('auth/register', async (userData, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/signup`, userData);

    if (response.data.requiresVerification) {
      return response.data;
    }

    if (response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.token);
    }

    return response.data;
  } catch (error) {
    const message = getErrorMessage(error) || 'Registration failed';
    return thunkAPI.rejectWithValue(message);
  }
});

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ email, code }, thunkAPI) => {
    try {
      const response = await axios.post(`${API_URL}/verify-email`, { email, code });

      if (response.data.requiresTwoFactor) {
        return thunkAPI.rejectWithValue({
          message: response.data.message,
          requiresTwoFactor: true,
          requiresVerification: false,
        });
      }

      if (response.data.accessToken) {
        tokenManager.setTokens(
          response.data.accessToken,
          response.data.refreshToken,
          response.data.expiresIn
        );
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('token', response.data.accessToken); // Keep for backward compatibility
      }

      return response.data;
    } catch (error) {
      const message = getErrorMessage(error) || 'Verification failed';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const login = createAsyncThunk('auth/login', async (userData, thunkAPI) => {
  try {
    const deviceInfo = deviceManager.getDeviceInfo();
    const loginData = {
      ...userData,
      ...deviceInfo,
    };

    const response = await axios.post(`${API_URL}/login`, loginData);

    if (response.data.requiresTwoFactor) {
      return thunkAPI.rejectWithValue({
        message: response.data.message,
        requiresTwoFactor: true,
      });
    }

    if (response.data.accessToken) {
      tokenManager.setTokens(
        response.data.accessToken,
        response.data.refreshToken,
        response.data.expiresIn
      );
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.accessToken); // Keep for backward compatibility
    }

    return response.data;
  } catch (error) {
    const message = getErrorMessage(error) || 'Login failed';
    const requiresTwoFactor = error.response?.data?.requiresTwoFactor || false;
    const requiresVerification = error.response?.data?.requiresVerification || false;
    const verificationEmail = error.response?.data?.user?.email || userData?.email || '';
    return thunkAPI.rejectWithValue({
      message,
      requiresTwoFactor,
      requiresVerification,
      verificationEmail,
    });
  }
});

export const loginWith2FA = createAsyncThunk(
  'auth/loginWith2FA',
  async ({ email, password, twoFactorToken, backupCode, trustDevice }, thunkAPI) => {
    try {
      const deviceInfo = deviceManager.getDeviceInfo();
      const loginData = {
        email,
        password,
        twoFactorToken,
        backupCode,
        trustDevice,
        ...deviceInfo,
      };

      const response = await axios.post(`${API_URL}/login`, loginData);

      if (response.data.accessToken) {
        tokenManager.setTokens(
          response.data.accessToken,
          response.data.refreshToken,
          response.data.expiresIn
        );
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('token', response.data.accessToken); // Keep for backward compatibility
      }

      return response.data;
    } catch (error) {
      const message = getErrorMessage(error) || '2FA verification failed';
      return thunkAPI.rejectWithValue({ message, requiresTwoFactor: false });
    }
  }
);

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (email, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/forgot-password`, { email });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error) || 'Failed to send reset email';
    return thunkAPI.rejectWithValue(message);
  }
});

export const resetPassword = createAsyncThunk('auth/resetPassword', async (resetData, thunkAPI) => {
  try {
    const response = await axios.put(`${API_URL}/reset-password/${resetData.token}`, {
      password: resetData.password,
    });

    if (response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.token);
    }

    return response.data;
  } catch (error) {
    const message = getErrorMessage(error) || 'Password reset failed';
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, thunkAPI) => {
    try {
      // Import api at runtime to avoid circular dependency
      const { default: api } = await import('../../utils/api');

      const response = await api.put(`${API_URL}/profile`, profileData);

      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      const message = getErrorMessage(error) || 'Profile update failed';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const dismissWelcome = createAsyncThunk('auth/dismissWelcome', async (_, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    const response = await axios.put(`${API_URL}/dismiss-welcome`, {}, config);

    if (response.data.success) {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        user.welcomeModalDismissed = true;
        localStorage.setItem('user', JSON.stringify(user));
      }
    }

    return response.data;
  } catch (error) {
    const message = getErrorMessage(error) || 'Failed to update preferences';
    return thunkAPI.rejectWithValue(message);
  }
});

export const impersonate = createAsyncThunk('auth/impersonate', async (userId, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    const response = await axios.post(`${API_URL}/impersonate`, { userId }, config);

    if (response.data.accessToken) {
      tokenManager.setTokens(
        response.data.accessToken,
        response.data.refreshToken,
        response.data.expiresIn
      );
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.accessToken);
      localStorage.setItem('originalUser', JSON.stringify(response.data.originalUser));
      localStorage.setItem('impersonating', 'true');
    }

    return {
      ...response.data,
      originalUser: response.data.originalUser,
    };
  } catch (error) {
    const message = getErrorMessage(error) || 'Impersonation failed';
    return thunkAPI.rejectWithValue(message);
  }
});

export const stopImpersonate = createAsyncThunk('auth/stopImpersonate', async (_, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    const response = await axios.post(`${API_URL}/stop-impersonate`, {}, config);

    if (response.data.accessToken) {
      tokenManager.setTokens(
        response.data.accessToken,
        response.data.refreshToken,
        response.data.expiresIn
      );
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.accessToken);
      localStorage.removeItem('originalUser');
      localStorage.removeItem('impersonating');
    }

    return response.data;
  } catch (error) {
    const message = getErrorMessage(error) || 'Failed to stop impersonation';
    return thunkAPI.rejectWithValue(message);
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('originalUser');
  localStorage.removeItem('impersonating');
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: state => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = '';
      state.requiresTwoFactor = false;
      state.requiresVerification = false;
      state.verificationEmail = '';
    },
    updateUserAvatar: (state, action) => {
      if (state.user) {
        state.user.avatarUrl = action.payload;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(checkAuth.pending, state => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.impersonating = action.payload.impersonating || false;
        state.originalUser = action.payload.originalUser || null;
      })
      .addCase(checkAuth.rejected, state => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.impersonating = false;
        state.originalUser = null;
      })
      .addCase(register.pending, state => {
        state.isLoading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.requiresVerification = action.payload.requiresVerification || false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.message = action.payload.message;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
        state.token = null;
      })
      .addCase(login.pending, state => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.requiresTwoFactor = false;
        state.requiresVerification = false;
        state.verificationEmail = '';
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.message = action.payload.message;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message =
          typeof action.payload === 'object' ? action.payload.message : action.payload;
        state.requiresTwoFactor = action.payload?.requiresTwoFactor || false;
        state.requiresVerification = action.payload?.requiresVerification || false;
        state.verificationEmail = action.payload?.verificationEmail || '';
        state.user = null;
        state.token = null;
      })
      .addCase(loginWith2FA.pending, state => {
        state.isLoading = true;
      })
      .addCase(loginWith2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.user;
        state.token = action.payload.accessToken || action.payload.token;
        state.message = action.payload.message;
        state.requiresTwoFactor = false;
        state.requiresVerification = false;
        state.verificationEmail = '';
      })
      .addCase(loginWith2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message =
          typeof action.payload === 'object' ? action.payload.message : action.payload;
        state.user = null;
        state.token = null;
      })
      .addCase(logout.fulfilled, state => {
        state.user = null;
        state.token = null;
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(forgotPassword.pending, state => {
        state.isLoading = true;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = action.payload.message;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(resetPassword.pending, state => {
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.message = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateProfile.pending, state => {
        state.isLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.user;
        state.message = action.payload.message;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(verifyEmail.pending, state => {
        state.isLoading = true;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.requiresTwoFactor = false;
        state.requiresVerification = false;
        state.verificationEmail = '';
        state.user = action.payload.user;
        state.token = action.payload.accessToken || action.payload.token;
        state.message = action.payload.message;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message =
          typeof action.payload === 'object' ? action.payload.message : action.payload;
        state.requiresTwoFactor = action.payload?.requiresTwoFactor || false;
        state.requiresVerification = action.payload?.requiresVerification || false;
      })
      .addCase(dismissWelcome.fulfilled, state => {
        if (state.user) {
          state.user.welcomeModalDismissed = true;
        }
      })
      .addCase(impersonate.pending, state => {
        state.isLoading = true;
      })
      .addCase(impersonate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.impersonating = true;
        state.originalUser = action.payload.originalUser;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.message = action.payload.message;
      })
      .addCase(impersonate.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(stopImpersonate.pending, state => {
        state.isLoading = true;
      })
      .addCase(stopImpersonate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.impersonating = false;
        state.originalUser = null;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.message = action.payload.message;
      })
      .addCase(stopImpersonate.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset, updateUserAvatar } = authSlice.actions;
export default authSlice.reducer;
